import { useQuery } from '@apollo/client';
import {
  AccessControl,
  AppContext,
  AsyncPropSelector,
  BackgroundIcon,
  DateTimeFormatter,
  isAuthorised,
  Loader,
  WorklogContainer,
  getTranslatedText,
  SkeletonLoading,
  toast,
  AddProducts,
  TAB_PAGES,
  useIsAuthorisedHook,
  useCheckFeatureGating
} from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  AccountClient,
  GET_ALL_TECHS_NEW
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { ActionsBtn } from '@nile/projects/src/Components/Elements';
import {
  DateCell,
  SelectCell,
  TextCell
} from '@nile/projects/src/Components/Inputs';
import {
  ButtonGroup,
  Button,
  Avatar,
  Checkbox
} from '@superops/ui-design-system';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { UPDATE_SHOW_PROJECT_TO_CLIENT } from '@nile/projects/src/Graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import dompurify from 'dompurify';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { getClientSiteVariablesById } from '@nile/graphql/src/Constants/variablesResolver';
import { ProjectServiceCatalogModal } from '@nile/client_management/src/Contracts/DeliveryMap/ServiceCatalog/ModuleBasedServiceCatalogModal';
import { GET_CLIENT_SITE_LIST_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/site';

const IconWrapper = styled.div`
  opacity: 0.6;
  margin-right: 5px;
`;

const OpacityWrapper = styled.div`
  opacity: 0.6;
`;

const ProjectHeader = styled.div`
  justify-content: space-around;
  background-color: var(--sops-bg-white);
  .title-holder {
    line-height: 1.3;
  }
  .gap40 {
    gap: 40px;
  }
  .maxw200 {
    max-width: 200px;
  }
  .show-to-client {
    -webkit-line-clamp: 2;
    white-space: inherit;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
  .projectViewBtns {
    .Superops-ButtonGroup {
      flex-wrap: nowrap !important;
    }
    /* width: 22%; */
  }
  .logoHolder {
    /* width: 8%; */
    /* padding: 0 1rem; */
    .projectLogo {
      width: 70px;
      height: 70px;
    }
  }

  .label {
    font-size: 14px;
    opacity: 0.6;
  }
  .id-svg {
    position: relative;
    top: 5px;
  }
  .name {
    font-size: 14px;
    font-weight: 700;
    align-items: center;
    .Superops-Avatar {
      min-width: 1.8rem;
    }
    .Superops-Avatar--sizeExtraSmall {
      width: 2rem;
      margin-right: 5px;
      // margin-top: 5px;
    }
  }
  .title {
    font-size: 24px;
    font-weight: 700;
    -webkit-line-clamp: 2;
    white-space: inherit;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 40vw;
    cursor: pointer;
  }
  /* .w70 {
    width: 70%;
  } */
  .space-btwn {
    justify-content: space-between;
  }
  .detail {
    // flex: 1 1;
    /* min-width: 200px;
    max-width: 200px;
    margin-right: 12px; */
    // &.dueHolder {
    //   min-width: 280px;
    //   max-width: none;
    // }
  }
  .statusBtnHolder {
    .Superops-Button--primary {
      min-width: 145px;
      padding: 0 1rem !important;
      margin: 0 1rem;
      &.no-access {
        min-width: 125px;
      }
      .Superops-Button__Content {
        .Superops-Button__Text {
          padding: 0;
          .value_selector__control {
            width: 120px;
            background: transparent;
            border: none !important;
            .react-select-option {
              color: var(--sops-asyncselect-text-color);
            }
          }
        }
      }
    }
  }
  .mr5 {
    margin-right: 5px;
  }
`;

const Value = styled.div`
  line-height: 16px;
  font-weight: bold;
  color: ${(props) => (props.link ? 'blue' : 'inherit')};
  white-space: nowrap;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: ${(props) => (props.link ? 'pointer' : 'default')};
`;

const DetailsComponent = (props) => {
  const { children, type, value } = props;
  return (
    <>
      <Tooltip
        html={
          <>
            <div className="tooltip-wrapper">
              <p>{type}</p>
            </div>
          </>
        }
        delay={200}
        duration={250}
        position="bottom"
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        distance={10}
      >
        <div className="cp">{children}</div>
      </Tooltip>
    </>
  );
};

const ProjectDetailHeader = ({
  project,
  handleUpdate,
  setProject,
  view,
  setView,
  taskTemplates,
  intl
}) => {
  const flags = useFlags();
  const projectContext = useContext(ProjectContext);
  const appContext = useContext(AppContext);
  const ticketContext = useContext(TicketContext);
  const [showToClient, setShowToClient] = useState(
    project.showToClient || false
  );
  const { company } = appContext;
  const { data: entityFields, loading, error } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const isNewContracts = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  useEffect(() => {
    // Clear items to invoice from project on  mount if it's present
    ticketContext?.getItemsToInvoice('WORKLOGS', null);
    ticketContext?.getItemsToInvoice('PRODUCTS', null);
  }, []);

  const dynamicTechnicianQuery = () =>
    AccountClient.query({
      query: GET_ALL_TECHS_NEW,
      variables: {
        input: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      }
    });

  const [updateShowClient] = useMutation(UPDATE_SHOW_PROJECT_TO_CLIENT, {
    onCompleted({ updateProject }) {
      if (updateProject) {
        setShowToClient(updateProject.showToClient);
        if (updateProject.showToClient)
          toast('success', 'project.share.success.message');
      }
    }
  });

  const generateProjectOwners = (field, data) => {
    // const options =
    //   data?.getAllTechnicians?.map((tech) => ({
    //     ...tech,
    //     value: tech.userId,
    //     label: tech.name
    //   })) || [];
    // FEAT - LIST ONLY MEMBERS FOR OWNER DROPDOWN
    const options = project?.members?.map((member) => ({
      ...member.user,
      value: member.user.userId,
      label: member.user.name
    }));
    return options;
  };

  const redirectToInvoice = () => {
    ticketContext?.openTab({
      module: TAB_PAGES.newInvoiceFromSource,
      id: project?.projectId,
      data: {
        module: 'PROJECT',
        action: 'generateInvoiceFromProject',
        project: project,
        productsToInvoice: ticketContext?.productsToInvoice
      }
    });
  };

  if (loading)
    return (
      <SkeletonLoading
        hideStepNav={true}
        hideTable={true}
        hideSecondaryHeader={true}
      />
    );
  if (error) return <div>Error...</div>;

  const allEntityFields =
    entityFields.getEntitiesMeta.find((el) => el.name === 'Project')
      ?.attributes || [];

  const {
    title,
    client,
    owner,
    site,
    dueDate,
    scheduledStartDate,
    status,
    loggedInUserRole,
    projectId,
    members,
    displayId
  } = project;

  const dynamicSiteQuery = () => {
    const siteVariables = getClientSiteVariablesById(client?.accountId);
    return AccountClient.query({
      query: GET_CLIENT_SITE_LIST_FOR_DROPDOWN,
      variables: siteVariables
    });
  };

  const generateProjectSiteOptions = (f, data) => {
    return (
      data?.getClientSiteList?.sites?.map((site) => ({
        name: site?.name,
        value: site?.id,
        label: site?.name
      })) || []
    );
  };

  const showProjectToClient = (data) => {
    updateShowClient({
      variables: {
        projectId,
        showToClient: data
      }
    });
  };

  // const isEditable = loggedInUserRole?.roleId === '6';
  return (
    <ProjectHeader className="full-width full-height flex p20">
      <div className="logoHolder flex jctr actr mr30">
        <SVGIcon name="ProjectFilled" size="xxl" />
      </div>
      <div className="fx fcol fg1">
        <div className="flex mb-10 center title-holder">
          <Tooltip
            html={<>{dompurifier(title)}</>}
            delay={100}
            duration={250}
            position="bottom"
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
            distance={5}
          >
            <div data-qa="projectTitle">
              <TextCell
                value={title}
                handleUpdate={handleUpdate}
                property="title"
                editable={isAuthorised(
                  projectContext.features,
                  'projectManagementProject.edit'
                )}
              />
            </div>
          </Tooltip>
          <div
            className="fx info jend projectViewBtns mlauto"
            data-qa="projectViewBtns"
          >
            <AccessControl
              productToCheck="MSP"
              render={
                <>
                  {isAuthorised(
                    projectContext.features,
                    'projectManagementProject.edit'
                  ) && (
                    <Tooltip
                      title={getTranslatedText(
                        intl,
                        'project.show.project.to.client'
                      )}
                      delay={100}
                      duration={250}
                      position="bottom"
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                      distance={5}
                    >
                      <div className="flex center mr-40">
                        <Checkbox
                          label={
                            <div className="ellipsis show-to-client">
                              <FormattedMessage id="project.show.project.to.client" />
                            </div>
                          }
                          checked={showToClient}
                          onChange={showProjectToClient}
                        />
                      </div>
                    </Tooltip>
                  )}
                </>
              }
            />
            <ButtonGroup>
              <ButtonGroup segmented>
                <Button
                  monochrome
                  outline={view === 'detail'}
                  onClick={() => setView('detail')}
                  icon={
                    <OpacityWrapper
                      title={getTranslatedText(
                        intl,
                        'project.details.view.list'
                      )}
                    >
                      <SVGIcon name="ListView" size="sm" />
                    </OpacityWrapper>
                  }
                />
                <Button
                  monochrome
                  outline={view === 'kanban'}
                  onClick={() => setView('kanban')}
                  icon={
                    <OpacityWrapper
                      title={getTranslatedText(
                        intl,
                        'project.details.view.kanban'
                      )}
                    >
                      <SVGIcon name="KanbanView" size="sm" />
                    </OpacityWrapper>
                  }
                />
              </ButtonGroup>
              <AccessControl
                roleToCheck="invoice.add"
                render={
                  <Button
                    icon={<SVGIcon name="InvoiceLogo" />}
                    onClick={redirectToInvoice}
                    disabled={
                      !ticketContext?.productsToInvoice ||
                      (!ticketContext?.productsToInvoice?.products &&
                        !ticketContext?.productsToInvoice?.worklogs)
                    }
                  >
                    <div className="ellipsis">
                      {getTranslatedText(intl, 'quote.details.generateInvoice')}
                    </div>
                  </Button>
                }
              />

              <AccessControl
                reference={projectContext.features}
                roleToCheck={{
                  roles: ['projectWorklog.add', 'serviceCatalog.view']
                }}
                render={
                  <div className="ml8">
                    {projectId ? (
                      isNewContracts ? (
                        <ProjectServiceCatalogModal project={project} />
                      ) : (
                        <AddProducts
                          id={projectId}
                          project={project}
                          client={project?.client}
                          module="PROJECT"
                        />
                      )
                    ) : null}
                  </div>
                }
              />
              {(isAuthorised(projectContext.features, 'task.add') ||
                isAuthorised(projectContext.features, 'milestone.add') ||
                isAuthorised(
                  projectContext.features,
                  'projectManagementProject.edit'
                )) && (
                <div data-qa="projectActionsBtn">
                  <ActionsBtn
                    client={client}
                    site={site}
                    title={title}
                    projectId={projectId}
                    members={members}
                    owner={owner}
                    handleUpdate={handleUpdate}
                    setProject={setProject}
                    taskTemplates={taskTemplates}
                  />
                </div>
              )}
              <div className="statusBtnHolder" data-qa="projectStatusBtn">
                <AccessControl
                  reference={projectContext.features}
                  roleToCheck="projectManagementProject.edit"
                  render={
                    <Button
                      primary
                      icon={
                        <div className="pr10">
                          <SVGIcon name="ProjectLogo" color="#fff" />
                        </div>
                      }
                    >
                      <AsyncPropSelector
                        defaultValue={{ label: status, value: status }}
                        value={status}
                        updateSelection={(field, val) =>
                          handleUpdate('status', val.value)
                        }
                        field={allEntityFields.find(
                          (field) => field.name === 'status'
                        )}
                        entity="Project"
                        hideTitle
                        isDark
                      />
                    </Button>
                  }
                  fallback={
                    <Button
                      primary
                      icon={<SVGIcon name="ProjectLogo" color="#fff" />}
                    >
                      <div>{status}</div>
                    </Button>
                  }
                />
              </div>
            </ButtonGroup>
          </div>
        </div>
        <div className="flex actr gap40">
          <DetailsComponent
            type={getTranslatedText(intl, 'attribute.projectId')}
          >
            <div className="detail">
              <div className="name fx">
                <div className="id-svg">
                  <SVGIcon name="Numbers" color="#666666" size="lg" />
                </div>
                <div className="ellipsis maxw200">{`#${displayId}`}</div>
              </div>
            </div>
          </DetailsComponent>

          <AccessControl
            productToCheck="MSP"
            render={
              <>
                <DetailsComponent
                  type={getTranslatedText(intl, 'common.client')}
                >
                  <div className="detail">
                    <div className="name fx">
                      <IconWrapper
                        title={getTranslatedText(
                          intl,
                          'project.details.label.client'
                        )}
                      >
                        <SVGIcon name="ClientLogo" />
                      </IconWrapper>

                      <div className="ptr">
                        <Value
                          className="sops-no-bold"
                          link={isAuthorised(
                            projectContext.features,
                            'client.view'
                          )}
                          onClick={() => {
                            if (
                              isAuthorised(
                                projectContext.features,
                                'client.view'
                              )
                            ) {
                              ticketContext.openTab({
                                module: 'CLIENT',
                                data: {
                                  id: client?.accountId,
                                  name: client?.name,
                                  value: client?.name
                                }
                              });
                            }
                          }}
                        >
                          <div className="ellipsis maxw200">
                            {client?.name || 'NA'}
                          </div>
                        </Value>
                      </div>
                    </div>
                  </div>
                </DetailsComponent>
              </>
            }
          />
          <DetailsComponent
            type={getTranslatedText(intl, 'client.details.user.site')}
          >
            <div className="detail" data-qa="projectSite">
              <div className="name fx">
                <IconWrapper
                  title={getTranslatedText(intl, 'project.details.label.site')}
                >
                  <SVGIcon name="Site" />
                </IconWrapper>
                <SelectCell
                  value={site}
                  handleUpdate={handleUpdate}
                  property="site"
                  allEntityFields={allEntityFields}
                  isDynamicQuery
                  dynamicQuery={dynamicSiteQuery}
                  generateOptions={generateProjectSiteOptions}
                  editable={isAuthorised(
                    projectContext.features,
                    'projectManagementProject.edit'
                  )}
                  variables={getClientSiteVariablesById(client?.accountId)}
                />
              </div>
            </div>
          </DetailsComponent>
          <div className="detail" data-qa="projectOwner">
            <DetailsComponent
              type={getTranslatedText(intl, 'project.view.owner')}
            >
              <div className="name fx">
                <span
                  title={getTranslatedText(
                    intl,
                    'project.details.label.projectOwner'
                  )}
                >
                  <Avatar customer name="Owner" size="extraSmall" />
                </span>
                <span>
                  <SelectCell
                    value={owner}
                    handleUpdate={handleUpdate}
                    property="owner"
                    allEntityFields={allEntityFields}
                    editable={isAuthorised(
                      projectContext.features,
                      'projectManagementProject.edit'
                    )}
                    isDynamicQuery
                    dynamicQuery={dynamicTechnicianQuery}
                    generateOptions={generateProjectOwners}
                  />
                </span>
              </div>
            </DetailsComponent>
          </div>
          <DetailsComponent
            type={getTranslatedText(intl, 'project.view.dueDate')}
          >
            <div className="detail dueHolder" data-qa="projectDueDate">
              <div className="name fx">
                <IconWrapper
                  title={getTranslatedText(
                    intl,
                    'project.details.label.dueDate'
                  )}
                >
                  <SVGIcon name="DatePicker" />
                </IconWrapper>
                <DateCell
                  value={dueDate}
                  handleUpdate={handleUpdate}
                  property="dueDate"
                  editable={isAuthorised(
                    projectContext.features,
                    'projectManagementProject.edit'
                  )}
                />
              </div>
            </div>
          </DetailsComponent>
        </div>
      </div>
    </ProjectHeader>
  );
};

export default injectIntl(ProjectDetailHeader);
